import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  useToast,
  Container,
  Heading,
  Text,
  Link,
  HStack,
  InputGroup,
  InputRightElement,
  IconButton,
  useColorModeValue,
  Card,
  CardBody,
  Divider,
  Alert,
  AlertIcon,
  Spinner,
  Checkbox,
  Image
} from '@chakra-ui/react'
import { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { FiEye, FiEyeOff, FiMail, FiLock } from 'react-icons/fi'
import { useAuth } from '../../../contexts/AuthContext'

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  })
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})
  
  const { login } = useAuth()
  const toast = useToast()

  // Color mode values
  const bg = useColorModeValue('gray.50', 'gray.900')
  const cardBg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.600')

  const validateForm = () => {
    const newErrors = {}
    
    if (!formData.email) {
      newErrors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required'
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) return
    
    setLoading(true)
    try {
      await login(formData.email, formData.password)
      toast({
        title: 'Welcome back!',
        description: 'You have successfully logged in',
        status: 'success',
        duration: 3000,
      })
    } catch (error) {
      toast({
        title: 'Login Failed',
        description: error.message || 'Invalid email or password',
        status: 'error',
        duration: 5000,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box minH="100vh" bg={bg} display="flex" alignItems="center" justifyContent="center" p={4}>
      <Container maxW="lg" py={8} px={6} bg={bg} borderRadius="md" boxShadow="lg">
        <VStack spacing={8} align="stretch">
          {/* Logo/Brand */}
          <Box textAlign="center">
            <Heading size="2xl" mb={2} bgGradient="linear(to-r, blue.400, purple.500)" bgClip="text">
              CMS Dashboard
            </Heading>
            <Text color="gray.500" fontSize="lg">
              Welcome back! Please sign in to your account
            </Text>
          </Box>

          {/* Login Card */}
          <Card bg={cardBg} borderColor={borderColor} variant="outline" shadow="xl">
            <CardBody p={8}>
              <form onSubmit={handleSubmit}>
                <VStack spacing={6} align="stretch">
                  {/* Email Field */}
                  <FormControl isRequired isInvalid={!!errors.email}>
                    <FormLabel>Email Address</FormLabel>
                    <InputGroup>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="Enter your email"
                        size="lg"
                        pr="3rem"
                      />
                      <InputRightElement h="3rem" pr={3}>
                        <FiMail color="gray.400" />
                      </InputRightElement>
                    </InputGroup>
                    {errors.email && (
                      <Text color="red.500" fontSize="sm" mt={1}>
                        {errors.email}
                      </Text>
                    )}
                  </FormControl>

                  {/* Password Field */}
                  <FormControl isRequired isInvalid={!!errors.password}>
                    <FormLabel>Password</FormLabel>
                    <InputGroup>
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        placeholder="Enter your password"
                        size="lg"
                        pr="3rem"
                      />
                      <InputRightElement h="3rem" pr={3}>
                        <IconButton
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowPassword(!showPassword)}
                          icon={showPassword ? <FiEyeOff /> : <FiEye />}
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        />
                      </InputRightElement>
                    </InputGroup>
                    {errors.password && (
                      <Text color="red.500" fontSize="sm" mt={1}>
                        {errors.password}
                      </Text>
                    )}
                  </FormControl>

                  {/* Remember Me & Forgot Password */}
                  <HStack justify="space-between">
                    <Checkbox
                      isChecked={formData.rememberMe}
                      onChange={(e) => handleInputChange('rememberMe', e.target.checked)}
                      colorScheme="blue"
                    >
                      <Text fontSize="sm">Remember me</Text>
                    </Checkbox>
                    <Link
                      as={RouterLink}
                      to="/forgot-password"
                      color="blue.500"
                      fontSize="sm"
                      _hover={{ textDecoration: 'underline' }}
                    >
                      Forgot password?
                    </Link>
                  </HStack>

                  {/* Login Button */}
                  <Button
                    type="submit"
                    colorScheme="blue"
                    size="lg"
                    width="100%"
                    isLoading={loading}
                    loadingText="Signing in..."
                    disabled={loading}
                    _hover={{ transform: 'translateY(-1px)', shadow: 'lg' }}
                    transition="all 0.2s"
                  >
                    Sign In
                  </Button>

                  {/* Divider */}
                  <HStack>
                    <Divider />
                    <Text px="3" color="gray.500" fontSize="sm" whiteSpace="nowrap">
                      Don't have an account?
                    </Text>
                    <Divider />
                  </HStack>

                  {/* Sign Up Link */}
                  <Button
                    as={RouterLink}
                    to="/signup"
                    variant="outline"
                    size="lg"
                    width="100%"
                    _hover={{ transform: 'translateY(-1px)', shadow: 'md' }}
                    transition="all 0.2s"
                  >
                    Create New Account
                  </Button>
                </VStack>
              </form>
            </CardBody>
          </Card>

          {/* Footer */}
          <Text textAlign="center" color="gray.500" fontSize="sm">
            By signing in, you agree to our{' '}
            <Link color="blue.500" href="#" _hover={{ textDecoration: 'underline' }}>
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link color="blue.500" href="#" _hover={{ textDecoration: 'underline' }}>
              Privacy Policy
            </Link>
          </Text>
        </VStack>
      </Container>
    </Box>
  )
}

export default Login